export const DietMealprep = (json: IDietMealprep): IDietMealprepModel => {
	const fromJSON = (itemJson: IDietMealprep) => {
		const { ...rest } = itemJson
		return {
			...rest,
		}
	}

	return { ...fromJSON(json) }
}

export const dietMealprepToJSON = (obj: Partial<IDietMealprepModel>): Partial<IDietMealprep> => {
	const { ...rest } = obj
	return {
		...rest,
	}
}

export const dietMealprepProductionStage = (json: IDietFoodProductionStage): IDietFoodProductionStageModel => {
	const { is_active, created_at, updated_at, ...rest } = json

	return {
		...rest,
		isActive: is_active,
		createdAt: created_at,
		updatedAt: updated_at,
	}
}

export const DietMealprepComponentToIngredient = (component: IDietMealComponentModel): IDietMealIngredientModel => {
	const { id, nutritionalParameters, pivot } = component

	let weight: number
	if (pivot) {
		weight = Number.isNaN(Number.parseFloat(pivot.weightAfterProcessing))
			? 0
			: Number.parseFloat(pivot.weightAfterProcessing)
	} else {
		weight = 0
	}

	let baseWeight: number
	if (pivot) {
		baseWeight = Number.isNaN(Number.parseFloat(pivot.netWeight)) ? 0 : Number.parseFloat(pivot.netWeight)
	} else {
		baseWeight = 0
	}

	return {
		id,
		weight,
		baseWeight,
		component,
		nutritions: nutritionalParameters,
		packageType: 0,
		scale: 100,
		packageSizeForm: 0,
		packageTypeForm: 0,
	}
}

export const DietMealprepModelByPortionToVariantComponentPayload = (
	ingridient: IDietMealIngredientModel,
): IDietMealComponentPayload => {
	const { component, rawWeight, netWeight, weightAfterProcessing, type, group, packageSizeForm } = ingridient

	return {
		type,
		id: component.id,
		group: group ?? null,
		raw_weight: rawWeight,
		net_weight: netWeight,
		weight_after_processing: weightAfterProcessing,
		weight_unit: 'g',
		packaging_type_id: packageSizeForm?.value,
	}
}

export const DietMealprepModelByPortionToVariantPayload = (
	model: TDietMealsModelByPortion,
): IDietMealVariantPayload[] => {
	const variants: IDietMealVariantPayload[] = []

	Object.keys(model).forEach(key => {
		if (key === EMealSize.ALL) return

		const variant: IMealVariantComponentModel = model[key as EMealSize]

		if (!variant.variant) return

		const components: IDietMealComponentPayload[] = variant.ingridients.flatMap(ingridient => {
			if (!ingridient.group) return [DietMealprepModelByPortionToVariantComponentPayload(ingridient)]

			return ingridient.components?.map(s => DietMealprepModelByPortionToVariantComponentPayload(s)) ?? []
		})

		if (!variant.variant) return

		// TODO: Fix interface connected to packaging
		variants.push({
			id: variant.variant.id,
			meal_size_id: variant.variant.mealSizeId,
			is_active: !variant.variant.isDisabled,
			preparation_time: variant.variant.preparationTime,
			packaging: null,
			components,
		})
	})

	return variants
}

/**
 * Payloads
 */
const DietMealprepSharedToPayload = (
	event: IFormSubmitEvent<IDietMealFormData>,
	others: IDietMealPayloadMapperOptions,
) => {
	const { nameForKitchen, mealType, mealPriceTier, minOrderQty, maxOrderQty, serveWarm, serveCold, difficultyLevel } =
		event.values

	const { productionSteps, modelByPortion, filterTags, marketingTags, type, addonCategory, relatedMeals } = others

	const components = modelByPortion[EMealSize.ALL].ingridients.flatMap(ingridient => {
		if (!ingridient.group) return [DietMealprepModelByPortionToVariantComponentPayload(ingridient)]

		return ingridient.components?.map(s => DietMealprepModelByPortionToVariantComponentPayload(s)) ?? []
	})

	const mealKindBasedOnType = {
		[EMealDialogType.MEAL]: EMealKind.MEAL,
		[EMealDialogType.ADDON_MEAL]: EMealKind.MEAL,
		[EMealDialogType.MEALPREP]: EMealKind.MEALPREP,
		[EMealDialogType.ADDON_MEAL_PREP]: EMealKind.MEALPREP,
	}

	const categoryBasedOnType = {
		[EMealDialogType.MEAL]: EMealCategory.MEAL,
		[EMealDialogType.ADDON_MEAL]: EMealCategory.ADDON,
		[EMealDialogType.MEALPREP]: EMealCategory.MEAL,
		[EMealDialogType.ADDON_MEAL_PREP]: EMealCategory.ADDON,
	}

	const category = categoryBasedOnType[type]
	const meal_kind = mealKindBasedOnType[type]
	const addon_category = addonCategory === EAddonsCategoryTypes.SANDWICH ? addonCategory : null

	const related_meals = relatedMeals.map(id => ({ id, relation_type: EDietRelatedType.MEALPREP_TO_MEAL }))

	return {
		internal_name: nameForKitchen,
		meal_type: mealType,
		meal_price_tier: mealPriceTier,
		min_order_qty: minOrderQty === 0 ? null : minOrderQty,
		max_order_qty: maxOrderQty,
		serve_warm: serveWarm,
		serve_cold: serveCold,
		has_same_package_size: false,
		preparation_difficulty: difficultyLevel,
		is_active: true,
		food_production_stages: productionSteps ? productionSteps.map(step => DietProductionStepToPayload(step)) : [],
		variants: DietMealprepModelByPortionToVariantPayload(modelByPortion),
		filter_tags: filterTags.map(tag => tag.value),
		marketing_tags: marketingTags.map(tag => tag.value),
		meal_category: category,
		related_meals: related_meals ?? null,
		meal_kind,
		components,
		addon_category,
	}
}

export const DietMealprepUpdateToPayload = (
	updated: IDietMealModel,
	event: IFormSubmitEvent<IDietMealFormData>,
	others: IDietMealPayloadMapperOptions,
) => {
	const { nameForClient, nameShortcut, recipe, description } = event.values

	const shared = DietMealprepSharedToPayload(event, others)

	return {
		...shared,
		id: updated.id,
		name: updated.name ? LangContentToJson(updated.name, nameForClient) : generateLangContentArray(nameForClient),
		short_name: updated.shortName
			? LangContentToJson(updated.shortName, nameShortcut)
			: generateLangContentArray(nameShortcut),
		recipe: updated.recipe ? LangContentToJson(updated.recipe, recipe!) : generateLangContentArray(recipe ?? []),
		description:
			description && updated.description
				? LangContentToJson(updated.description, description)
				: generateLangContentArray(description),
	}
}

export const DietMealprepCreateToPayload = (
	event: IFormSubmitEvent<IDietMealFormData>,
	others: IDietMealPayloadMapperOptions,
) => {
	const { nameForClient, nameShortcut, recipe, description } = event.values

	const shared = DietMealprepSharedToPayload(event, others)

	return {
		...shared,
		name: generateLangContentArray(nameForClient),
		short_name: generateLangContentArray(nameShortcut),
		recipe: generateLangContentArray(recipe!),
		description: generateLangContentArray(description),
	}
}
